import React, { useEffect, useMemo, useState } from "react";
import { useAnyWbsUpdated } from "../../../../hooks/useWbsUpdated";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";

import { getPipingPlantProjects } from "./api";

import { usePiping } from "./PipingContext";

function LoadProjectDataComboBox({
  plantId,
  clientName,
  type,
  onSelect,
  disabled = false,
  size = "small",
  fullWidth = true,
}) {
  const { selectedProjectId, setSelectedProjectId, selectEstimationRevision } =
    usePiping();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  // This list is fetched once per plant, so a WBS renamed in the projects
  // tree would otherwise keep its old name in the dropdown - and its old
  // description under it - until the page is reloaded. Applied in place
  // rather than re-fetched, so it is there the moment the edit is saved.
  // Every WBS of the plant, not just the selected one: they are all
  // options here.
  useAnyWbsUpdated(({ projectId, changes }) => {
    const apply = (project) =>
      project && String(project.id) === String(projectId)
        ? { ...project, ...changes }
        : project;

    setProjects((prev) => (Array.isArray(prev) ? prev.map(apply) : prev));
    setSelectedProject((prev) => apply(prev));
  });

  const normalizeType = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const normalizedType = useMemo(() => normalizeType(type), [type]);

  const projectLabel = (project) => project?.name || project?.link || "Unnamed";

  const fetchProjects = async () => {
    if (!plantId) {
      setErr("Missing plantId.");
      setProjects([]);
      setSelectedProject(null);
      return;
    }

    if (!clientName) {
      setErr("Missing clientName.");
      setProjects([]);
      setSelectedProject(null);
      return;
    }

    if (!normalizedType) {
      setErr("Missing project type.");
      setProjects([]);
      setSelectedProject(null);
      return;
    }

    setLoading(true);
    setErr("");
    setProjects([]);
    setSelectedProject(null);

    try {
      let res;

      if (normalizedType === "pip") {
        res = await getPipingPlantProjects(plantId, clientName);
      } else {
        throw new Error(`Unsupported project type: ${normalizedType}`);
      }

      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];

      setProjects(list);

      if (!list.length) {
        setErr("No projects found.");
        return;
      }

      const currentSelected =
        list.find((project) => project?.id === selectedProjectId) || null;

      setSelectedProject(currentSelected);
    } catch (error) {
      setErr(error?.message || "Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [plantId, clientName, normalizedType]);

  useEffect(() => {
    if (!selectedProjectId || !projects.length) {
      return;
    }

    const currentSelected =
      projects.find((project) => project?.id === selectedProjectId) || null;

    setSelectedProject(currentSelected);
  }, [selectedProjectId, projects]);

  const handleChange = (_event, project) => {
    setErr("");
    setSelectedProject(project);

    // Selecting a project only sets which project is active - actual data
    // loading is deferred until a revision is confirmed (picked from the Rev.
    // dropdown, or opened from the projects tree), so a fresh WBS selection
    // doesn't clobber the previously loaded revision. loadPipingData runs off
    // selectedProjectId in PipingContext, exactly as PV's does.
    setSelectedProjectId(project.id);
    // Same as PV's setConfirmedRevision(null): drop the revision the previous
    // WBS was showing, so the page falls back to "Select a WBS UID and a
    // revision above" until one is picked for this WBS.
    selectEstimationRevision("");

    onSelect?.(project);
  };

  const isDisabled = disabled || loading;

  return (
    <Box sx={{ width: fullWidth ? "100%" : 360 }}>
      <Autocomplete
        size={size}
        fullWidth={fullWidth}
        disabled={isDisabled}
        loading={loading}
        options={projects}
        value={selectedProject}
        getOptionLabel={projectLabel}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        onChange={handleChange}
        noOptionsText={err || "No projects found"}
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: 20,
            height: 25,
            padding: "0 32px 0 8px !important",
            fontSize: 12,
          },

          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
            padding: "0 !important",
            height: 25,
            lineHeight: "25px",
            fontSize: 12,
          },

          "& .MuiInputLabel-root": {
            fontSize: 12,
            transform: "translate(14px, 5px) scale(1)",
          },

          "& .MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },

          "& .MuiAutocomplete-endAdornment": {
            top: "50%",
            transform: "translateY(-50%)",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={Boolean(err)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option?.id || option?.name}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
              {projectLabel(option)}
            </Typography>
          </Box>
        )}
      />
      {/* 3rd row: the selected WBS's description, mirroring the plant
          description that sits under Project# UID in the header. */}
      {selectedProject?.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={selectedProject.description}
          sx={{ display: "block", maxWidth: 260 }}
        >
          {selectedProject.description}
        </Typography>
      )}
    </Box>
  );
}

export default LoadProjectDataComboBox;
